import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Camera, Trash2, UploadCloud } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { workshopService } from "@/services/workshop.service";
import { uploadsService } from "@/services/uploads.service";
import { getErrorMessage } from "@/services/api";
import {
    WORKSHOP_PHOTO_CATEGORIES,
    WORKSHOP_PHOTO_CATEGORY_LABELS,
} from "@/config/constants";
import { formatDateTime, mediaUrl } from "@/lib/utils";

export function WorkshopPhotoUploader({ workshop }) {
    const qc = useQueryClient();
    const disabled = workshop?.workshop_status === "aguardando_proposta";

    const [category, setCategory] = useState(WORKSHOP_PHOTO_CATEGORIES[0].value);
    const [description, setDescription] = useState("");
    const [uploading, setUploading] = useState(false);

    const add = useMutation({
        mutationFn: (payload) => workshopService.addPhoto(workshop.id, payload),
        onSuccess: () => {
            toast.success("Foto adicionada.");
            qc.invalidateQueries({ queryKey: ["workshop", workshop.id] });
            setDescription("");
        },
        onError: (e) => toast.error(getErrorMessage(e, "Falha ao adicionar foto.")),
    });

    const remove = useMutation({
        mutationFn: (photoId) => workshopService.removePhoto(workshop.id, photoId),
        onSuccess: () => qc.invalidateQueries({ queryKey: ["workshop", workshop.id] }),
        onError: (e) => toast.error(getErrorMessage(e, "Falha ao remover foto.")),
    });

    async function handleFile(e) {
        const file = e.target.files?.[0];
        if (!file) return;
        e.target.value = "";  // reset input for re-selection of same file
        try {
            setUploading(true);
            const uploaded = await uploadsService.upload(file);
            await add.mutateAsync({
                url: uploaded.url,
                category,
                description: description || null,
            });
        } catch (err) {
            toast.error(getErrorMessage(err, "Falha ao enviar arquivo."));
        } finally {
            setUploading(false);
        }
    }

    const photos = workshop?.photos || [];

    // Group photos by category
    const grouped = photos.reduce((acc, p) => {
        (acc[p.category || "outro"] ||= []).push(p);
        return acc;
    }, {});

    return (
        <div className="space-y-4" data-testid="workshop-photos">
            {!disabled && (
                <div className="grid gap-3 rounded-md border bg-muted/20 p-3 md:grid-cols-[1fr_1fr_auto]">
                    <div className="space-y-1.5">
                        <Label className="text-xs font-medium">Categoria</Label>
                        <Select value={category} onValueChange={setCategory}>
                            <SelectTrigger data-testid="photo-category-picker"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {WORKSHOP_PHOTO_CATEGORIES.map((c) => (
                                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-xs font-medium">Descrição (opcional)</Label>
                        <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Ex: rotor com pontos de corrosão" data-testid="photo-description" />
                    </div>
                    <div className="flex items-end">
                        <label className="flex h-9 w-full cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed bg-background px-3 text-xs font-medium hover:border-secondary/60">
                            <UploadCloud className="h-4 w-4" />
                            {uploading ? "Enviando..." : "Escolher arquivo"}
                            <input type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid="photo-file-input" disabled={uploading} />
                        </label>
                    </div>
                </div>
            )}

            {photos.length === 0 ? (
                <div className="rounded-md border border-dashed p-6 text-center text-xs text-muted-foreground">
                    <Camera className="mx-auto mb-2 h-6 w-6 opacity-60" />
                    Nenhuma foto adicionada ainda.
                </div>
            ) : (
                <div className="space-y-4">
                    {Object.entries(grouped).map(([cat, items]) => (
                        <div key={cat}>
                            <div className="mb-2 flex items-center gap-2">
                                <Badge variant="outline" className="text-[10px]">
                                    {WORKSHOP_PHOTO_CATEGORY_LABELS[cat] || cat}
                                </Badge>
                                <span className="text-[11px] text-muted-foreground">{items.length} foto{items.length === 1 ? "" : "s"}</span>
                            </div>
                            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
                                {items.map((p) => (
                                    <div key={p.id} className="group relative overflow-hidden rounded-md border">
                                        <img
                                            src={mediaUrl(p.url)}
                                            alt={p.description || cat}
                                            className="aspect-square w-full object-cover"
                                            loading="lazy"
                                        />
                                        {(p.description || p.at) && (
                                            <div className="absolute inset-x-0 bottom-0 bg-black/60 p-2 text-[10px] text-white">
                                                {p.description && <div className="line-clamp-2">{p.description}</div>}
                                                <div className="opacity-80">{p.at ? formatDateTime(p.at) : ""}</div>
                                            </div>
                                        )}
                                        {!disabled && (
                                            <Button
                                                variant="secondary"
                                                size="icon"
                                                className="absolute right-1 top-1 h-7 w-7 opacity-0 shadow group-hover:opacity-100"
                                                onClick={() => remove.mutate(p.id)}
                                                data-testid={`remove-photo-${p.id}`}
                                            >
                                                <Trash2 className="h-3.5 w-3.5 text-danger" />
                                            </Button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default WorkshopPhotoUploader;
